import type { Location } from 'history';
import { isEmpty } from 'lodash/fp';
import qs from 'qs';
import { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom-v5-compat';
import { useQueryParam } from 'use-query-params';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import NullState from 'dashboard/components-lib/NullState';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { WorkflowBlockStatus } from 'dashboard/models/workflow-block';
import { WorkflowStepInput } from 'dashboard/models/workflow-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearWorkflowHistory, setInputStep, undoWorkflow } from 'dashboard/redux/actions/workflow';
import { useGetWorkflowBlockQuery } from 'dashboard/redux/api/workflow-block';
import {
  useGetWorkflowModuleQuery,
  usePropagateWorkflowModuleMutation,
  useUpdateWorkflowModuleMutation,
} from 'dashboard/redux/api/workflow-module';
import {
  useGetBaseWorkflowObjectJsonSchemasQuery,
  useGetWorkflowSchemaQuery,
} from 'dashboard/redux/api/workflow-schema';
import { setBlockData } from 'dashboard/redux/slices/workflow-block';
import { RootState } from 'dashboard/redux/store';
import {
  BlockUpdateData,
  publishWorkflowBlock,
  saveWorkflowBlock,
  setBlockEditorContext,
  setStepsConfigAndOutputs,
  validateWorkflowBlock,
} from 'dashboard/redux/thunk-actions/workflow-block';
import { fetchObjectJsonSchema } from 'dashboard/redux/thunk-actions/workflow-schema';
import { ConfigQueryParam } from 'dashboard/routes/DashboardWorkflowsShowRoute/types';
import DashboardWorkflowModulesShowView from 'dashboard/views/DashboardWorkflowModulesShowView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useBatchMutation from 'lib/hooks/useBatchMutation';
import useBatchQuery from 'lib/hooks/useBatchQuery';
import useDebounced from 'lib/hooks/useDebounced';
import useEventBus from 'lib/hooks/useEventBus';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { ApiModel } from 'lib/interfaces/api';

type Params = { workflowModuleId: string };
const DashboardWorkflowModulesShowRoute = (props: PropsFromRedux) => {
  const {
    blockData,
    canUndo,
    clearWorkflowHistory,
    currentEnvironmentType,
    inputStep,
    organization,
    saveWorkflowBlock,
    stepsConfig,
    setBlockData,
    setBlockEditorContext,
    setInputStep,
    setStepsConfigAndOutputs,
    showMessage,
    publishWorkflowBlock,
    undoWorkflow,
    validateWorkflowBlock,
    workflowVersionErrors,
  } = props;

  const { workflowModuleId } = useParams<Params>() as Params; // workflowModuleId will always be defined
  const { search: locationSearch } = useLocation();
  const navigate = useNavigate();

  const { version } = qs.parse(locationSearch, {
    ignoreQueryPrefix: true,
  }) as { version: string };
  const enablePlatformObjectJsonSchema = useFeatureFlag(
    'dashboard:workflow-use-platform-object-json-schema',
  );
  const enableWorkflowMethodChainV2Validation = useFeatureFlag(
    'dashboard:workflow-method-chain-v2-validation',
  );
  const workflowSchemaQuery = useGetWorkflowSchemaQuery({
    loadCriteriaVariableSchemas: !enablePlatformObjectJsonSchema,
    loadPrimitives: enableWorkflowMethodChainV2Validation,
  });
  const moduleQuery = useGetWorkflowModuleQuery(workflowModuleId);
  const [configQueryParam] = useQueryParam<ConfigQueryParam>('config');
  const [propagate, propagateMutation] = usePropagateWorkflowModuleMutation();
  const [updateWorkflowModule, updateWorkflowModuleMutation] = useUpdateWorkflowModuleMutation();

  const editableBlockId =
    moduleQuery.data?.data.relationships['latest-draft-block']?.data?.id ||
    moduleQuery.data?.data.relationships['latest-published-block']?.data?.id ||
    '';
  const workflowBlockId = version || editableBlockId;
  const blockQuery = useGetWorkflowBlockQuery(workflowBlockId, {
    skip: !workflowBlockId || !workflowSchemaQuery.data,
    refetchOnMountOrArgChange: true,
  });
  const basePlatformObjectSchemasQuery = useGetBaseWorkflowObjectJsonSchemasQuery(
    blockQuery.data?.data.attributes['template-filters'] ?? [],
    {
      skip:
        !blockQuery.data?.data.attributes['template-filters'] || !enablePlatformObjectJsonSchema,
      refetchOnMountOrArgChange: true,
    },
  );
  const isModulesEnabled = useFeatureFlag('global:enable-workflow-modules');
  const [debouncedValidateBlock] = useDebounced(validateWorkflowBlock, 333);
  const [isDirty, setIsDirty] = useState(false);

  if (!version && workflowBlockId) {
    navigate(`/dashboard/workflows/modules/${workflowModuleId}?version=${workflowBlockId}`, {
      replace: true,
    });
  }

  const { isLoadingAll, isFetchingAll } = useBatchQuery({
    queries: [moduleQuery, blockQuery, workflowSchemaQuery, basePlatformObjectSchemasQuery],
    onError: ({ requestId }) => {
      switch (requestId) {
        case moduleQuery.requestId:
          return { message: 'Failed to load module.' };
        case workflowSchemaQuery.requestId:
          return { message: 'Failed to load module schema.' };
        case blockQuery.requestId:
          return { message: 'Failed to load module version.' };
        case basePlatformObjectSchemasQuery.requestId:
          return { message: 'Failed to load module schema.' };
      }
    },
  });

  const { isLoadingAll: isLoadingMutations } = useBatchMutation({
    mutations: [propagateMutation, updateWorkflowModuleMutation],
    onError: ({ requestId, error }) => {
      switch (requestId) {
        case propagateMutation.requestId:
          return { message: 'Failed to update workflow module users.' };
        case updateWorkflowModuleMutation.requestId:
          return {
            message: (error?.errors?.at(0)?.details as string) || error.message,
          };
      }
    },
  });

  const { data: moduleResponse } = moduleQuery;
  const { data: blockResponse } = blockQuery;
  const { data: workflowSchema } = workflowSchemaQuery;
  const { data: platformObjectJsonSchema } = basePlatformObjectSchemasQuery;

  useEffect(() => {
    if (blockResponse?.data && (!enablePlatformObjectJsonSchema || platformObjectJsonSchema)) {
      setBlockEditorContext(blockResponse.data);
    }
  }, [
    blockResponse?.data,
    enablePlatformObjectJsonSchema,
    platformObjectJsonSchema,
    setBlockEditorContext,
  ]);

  const leavingRouteWithChanges = useCallback(
    (currentLocation: Location, nextLocation?: Location) => {
      // Navigating to, from, and within config modal should not prompt unsaved changes modal.
      // This is not ideal and we should instead centralize route handling.
      const toggleModalPath = (currPath?: string, nextPath?: string) =>
        currPath && nextPath
          ? currPath.includes(`${nextPath}/config`) || nextPath.includes('/config')
          : false;

      if (toggleModalPath(currentLocation?.pathname, nextLocation?.pathname)) {
        return false;
      }
      return isDirty;
    },
    [isDirty],
  );

  const loadPreviousBlock = useCallback(
    (versionId) => {
      navigate(`/dashboard/workflows/modules/${workflowModuleId}?version=${versionId}`, {
        replace: true,
      });
    },
    [navigate, workflowModuleId],
  );

  const updateVersionConfigAndValidate = useCallback(
    ({ 'steps-config': stepsConfig, ...newBlockData }: Partial<BlockUpdateData>) => {
      if (stepsConfig) {
        setStepsConfigAndOutputs(stepsConfig);
      }
      if (!isEmpty(newBlockData)) {
        setBlockData(newBlockData);
      }
      setIsDirty(true);
      debouncedValidateBlock();
    },
    [setStepsConfigAndOutputs, setBlockData, debouncedValidateBlock],
  );

  const updateInputStepAndValidate = useCallback(
    (inputStep: WorkflowStepInput) => {
      setInputStep(inputStep);
      setIsDirty(true);
      debouncedValidateBlock();
    },
    [setInputStep, debouncedValidateBlock],
  );

  const {
    callback: onSaveBlock,
    loading: saveBlockLoading,
    error: saveBlockError,
  } = useApiCallback(async () => {
    // We set state before saving because saving will cause redirects triggering the unsaved changes prompt
    if (isDirty) {
      setIsDirty(false);

      const updatedBlockId = await saveWorkflowBlock({
        id: workflowBlockId,
        inputs: inputStep.inputs,
        'steps-config': stepsConfig,
        parentType: ApiModel.WorkflowModule,
        parentId: workflowModuleId,
        isPublished: blockResponse?.data.attributes.status === WorkflowBlockStatus.Published,
        ...blockData,
      });
      if (!updatedBlockId) {
        return;
      }
      if (updatedBlockId !== workflowBlockId) {
        navigate(`/dashboard/workflows/modules/${workflowModuleId}?version=${updatedBlockId}`, {
          replace: true,
        });
      }
      showMessage('Successfully saved module as a draft.', MessageType.Success);
    }
  }, [
    isDirty,
    workflowBlockId,
    inputStep,
    stepsConfig,
    workflowModuleId,
    blockResponse?.data.attributes.status,
    blockData,
    saveWorkflowBlock,
    showMessage,
  ]);

  useRTKErrorHandler({
    error: saveBlockError,
    onError: () => ({ message: 'Failed to save module version.' }),
  });

  const notifyPublish = useEventBus('publish-module');

  const {
    callback: onPublishBlock,
    loading: publishBlockLoading,
    error: publishBlockError,
  } = useApiCallback(
    async (selectedWorkflows: string[]) => {
      setIsDirty(false);
      const propagationResults = await publishWorkflowBlock({
        id: workflowBlockId,
        parentType: ApiModel.WorkflowModule,
        parentId: workflowModuleId,
        description: blockData.description,
        workflowsToUpdate: selectedWorkflows,
      });
      showMessage('Successfully published module version.', MessageType.Success);
      notifyPublish(workflowModuleId);
      return propagationResults;
    },
    [
      workflowBlockId,
      workflowModuleId,
      blockData.description,
      propagate,
      publishWorkflowBlock,
      showMessage,
    ],
  );

  useRTKErrorHandler({
    error: publishBlockError,
    onError: (error) => {
      const errorMsg = error?.errors?.at(0)?.details;
      return {
        message: typeof errorMsg === 'string' ? errorMsg : 'Failed to publish module version.',
      };
    },
  });

  if (isLoadingAll) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen />
      </Backdrop>
    );
  }

  if (
    !blockResponse ||
    !moduleResponse ||
    !workflowSchema ||
    !inputStep ||
    !stepsConfig ||
    !isModulesEnabled
  ) {
    return (
      <Backdrop>
        <NullState
          message='We couldn’t find that Workflow Module.'
          backRoute='/dashboard/workflows/modules'
        />
      </Backdrop>
    );
  }

  return (
    <>
      <NavConfirmPrompt prevent={leavingRouteWithChanges} />
      <DashboardWorkflowModulesShowView
        blockData={blockData}
        blockId={blockResponse.data.id}
        blockStatus={blockResponse.data.attributes.status}
        canUndo={canUndo}
        configQueryParam={configQueryParam ?? null}
        currentEnvironmentType={currentEnvironmentType}
        inputStep={inputStep}
        isDirty={isDirty}
        loading={isFetchingAll || isLoadingMutations || saveBlockLoading || publishBlockLoading}
        organization={organization}
        editableBlockId={editableBlockId}
        stepsConfig={stepsConfig}
        validationErrors={workflowVersionErrors}
        workflowModule={moduleResponse.data}
        workflowSchema={workflowSchema}
        clearWorkflowHistory={clearWorkflowHistory}
        handleClickSave={onSaveBlock}
        handleClickPublish={onPublishBlock}
        loadPreviousBlock={loadPreviousBlock}
        setInputStep={updateInputStepAndValidate}
        showMessage={showMessage}
        undoWorkflowModule={undoWorkflow}
        updateWorkflowModule={async (newModule) => {
          await updateWorkflowModule(newModule).unwrap();
          showMessage('Successfully updated the module.', MessageType.Success);
        }}
        updateVersionConfig={updateVersionConfigAndValidate}
        fetchPlatformObjectJsonSchema={props.fetchObjectJsonSchema}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  blockData: state.workflowBlock.blockData,
  canUndo: state.workflow.past.length > 0,
  currentEnvironmentType: state.currentEnvironment.currentEnvironment.type,
  inputStep: state.workflow.present.inputStep,
  organization: state.organization.organization,
  stepsConfig: state.workflow.present.stepsConfig,
  workflowVersionErrors: state.workflow.present.workflowVersionErrors,
});

const mapDispatchToProps = {
  clearWorkflowHistory,
  saveWorkflowBlock,
  setBlockData,
  setBlockEditorContext,
  setInputStep,
  setStepsConfigAndOutputs,
  showMessage,
  publishWorkflowBlock,
  undoWorkflow,
  validateWorkflowBlock,
  fetchObjectJsonSchema,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardWorkflowModulesShowRoute);
